import { Currency } from "@redotech/money/currencies";
import { moneyFormat } from "@redotech/money/util";
import { Team } from "@redotech/redo-team-common/team";
import { stringify } from "csv-stringify/browser/esm/sync";
import { CoverageFilterValue } from "./teams-types";

export const isTeamAnonymized = (team: Team): boolean =>
  team.teamAnonymization?.wasAnonymized === true;

export const generateCSVData = (teams: Team[]): string => {
  const headers = ["Name", "Store URL", "Coverage", "Price"];
  const rows = teams.map((team) => [
    team.name,
    team.storeUrl,
    team.settings.coverageEnabled ? "TRUE" : "FALSE",
    team.pricePerOrder
      ? moneyFormat({ amount: +team.pricePerOrder, currency: Currency.USD })
      : "",
  ]);

  const csvContent = stringify([headers, ...rows]);

  return csvContent;
};

export const parseAndNormalizeStoreUrl = (storeUrl: string): URL | null => {
  if (!storeUrl || typeof storeUrl !== "string") {
    return null;
  }

  const trimmedUrl = storeUrl.trim();
  if (!trimmedUrl) {
    return null;
  }

  try {
    const urlWithProtocol =
      trimmedUrl.startsWith("http://") || trimmedUrl.startsWith("https://")
        ? trimmedUrl
        : `https://${trimmedUrl}`;

    return new URL(urlWithProtocol);
  } catch {
    return null;
  }
};

export const isValidStoreUrl = (storeUrl: string): boolean => {
  const url = parseAndNormalizeStoreUrl(storeUrl);
  return url !== null && url.hostname.length > 0;
};

export const buildShopifyAdminUrl = (storeUrl: string): string | null => {
  const url = parseAndNormalizeStoreUrl(storeUrl);
  return url ? `${url.protocol}//${url.hostname}/admin` : null;
};

// Redo Tech's Shopify dev dashboard (formerly Partners) organization.
const SHOPIFY_DEV_DASHBOARD_ORG_ID = "129004632";

const shopifyShopId = (team: Pick<Team, "_shopify">): string | null => {
  const id = team._shopify?.id;
  if (typeof id === "number" && Number.isInteger(id) && id > 0) {
    return String(id);
  }
  if (typeof id === "string" && /^[1-9]\d*$/.test(id)) {
    return id;
  }
  return null;
};
const isShopifyDevStore = (team: Pick<Team, "_shopify">): boolean => {
  const planDisplayName = team._shopify?.plan_display_name;
  return (
    typeof planDisplayName === "string" &&
    planDisplayName.includes("Development")
  );
};

/**
 * Logs the viewer into the store via Shopify's collaborator SSO, so the link
 * works even on a store they've never opened before.
 */
const buildShopifyCollaboratorLoginUrl = (
  team: Pick<Team, "_shopify">,
): string | null => {
  if (isShopifyDevStore(team)) {
    return null;
  }
  const shopId = shopifyShopId(team);
  return shopId
    ? `https://dev.shopify.com/dashboard/${SHOPIFY_DEV_DASHBOARD_ORG_ID}/stores/${shopId}/collaborator_login`
    : null;
};

/** Prefers the SSO login link, falling back to the legacy admin URL. */
export const buildShopifyAdminLoginUrl = (
  team: Pick<Team, "_shopify" | "storeUrl">,
): string | null =>
  buildShopifyCollaboratorLoginUrl(team) ?? buildShopifyAdminUrl(team.storeUrl);

export const buildCSVFilename = (
  search: string,
  coverageFilter: CoverageFilterValue,
): string => {
  const date = Temporal.Now.plainDateISO().toString();
  const parts = ["merchants", date];

  if (search.trim()) {
    // Sanitize search term for filename (remove special characters, limit length)
    const sanitizedSearch = search
      .replace(/[^a-zA-Z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .substring(0, 20);
    parts.push(`search-${sanitizedSearch}`);
  }

  if (coverageFilter !== "all") {
    parts.push(`coverage-${coverageFilter}`);
  }

  return parts.join("-");
};
